import type { PricingCampaignFormData } from '@/entities/pricing-campaign'
import type { ActionType } from '@/shared/components/ActionsDropdown'
import type { SettingsTemplateModel } from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { FieldErrors } from 'react-hook-form'

import { Suspense, useMemo } from 'react'
import { filter, pipe, prop, propOr } from 'lodash/fp'
import { AlertTriangleIcon, EllipsisVerticalIcon, InfoIcon } from 'lucide-react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
	usePricingCampaignContext,
	useSettingsActions,
} from '@/features/pricing-campaign/edit'
import { useTemplateActions } from '@/features/settings-template-actions'

import { useSettingsSchemasQuery } from '@/entities/core'
import { formatLegacyTactics } from '@/entities/pricing-rule'
import { VideoTutorials } from '@/entities/video-tutorial'

import { ActivityFields } from '@/widgets/pricing-campaign/activity'
import { AnchoringRulesFiledController } from '@/widgets/pricing-campaign/anchoring'
import { AssignmentFieldsController } from '@/widgets/pricing-campaign/assignment'
import { MinimalPriceStepFiledController } from '@/widgets/pricing-campaign/minimal-steps'
import { PriceLimitsFieldController } from '@/widgets/pricing-campaign/price-limits'
import {
	MarkdownPeriodsFieldController,
	PricingTacticDemandFields,
	PricingTacticMarkdowFields,
	PricingTacticRuleFieldController,
	PromoPeriodFieldController,
	TargetPriceTypeController,
} from '@/widgets/pricing-campaign/pricing-tactics'
import { RoundingRulesFiledController } from '@/widgets/pricing-campaign/rounding-rules'

import { ActionsDropdown } from '@/shared/components/ActionsDropdown'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { RHFSelect } from '@/shared/components/HookForm'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import { Loader } from '@/shared/components/ui/loader'
import { ModalBody } from '@/shared/components/ui/modal'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/ui/tabs'
import {
	ApplyAction,
	PricingEngineType,
	SettingsTemplateType,
	VideoTag,
} from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import {
	getEnumOptions,
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

import { PricingCampaignAssignmentPrediction } from './PricingCampaignAssignmentPrediction'
import {
	PricingCampaignAnchoringExampleSheet,
	PricingCampaignLimitsExampleSheet,
	PricingCampaignRoundingExampleSheet,
} from './PricingCampaignSettingsExample'

const NEW_PC_ACTION_KEYS = ['edit_name', 'use_template']
export const PricingCampaignFormBody = () => {
	const { t } = useTranslation()

	const context = usePricingCampaignContext()
	const tabs = useMemo(() => getTabs(t), [])

	return (
		<Tabs defaultValue='assignment' asChild>
			<ModalBody className='overflow-hidden' id='pc-modal-body'>
				<TabsList>
					{tabs.map((tab) => (
						<TabsTrigger
							data-testid='pc-modal-tab'
							data-tab={tab.key}
							key={tab.key}
							value={tab.key}
							onClick={() => {
								ampli.settingsOgModalTabClick({
									tab: tab.key,
								})
							}}
						>
							{tab.label}
							{tab.hasError(context.methods.formState.errors) && (
								<AlertTriangleIcon className='text-destructive' />
							)}
						</TabsTrigger>
					))}
				</TabsList>
				{tabs.map(({ TabContent, key }) => (
					<TabsContent
						tabIndex={1}
						data-testid='pc-modal-tab-content'
						className='-mx-4 flex flex-col overflow-y-auto px-4'
						value={key}
						key={key}
					>
						<TabContent />
					</TabsContent>
				))}
			</ModalBody>
		</Tabs>
	)
}

const getTabs = (t: TFunction) =>
	[
		{
			key: 'activity',
			label: t('settings.activity.short', { defaultValue: 'Activity' }),
			hasError: (errors: FieldErrors<PricingCampaignFormData>) =>
				errors?.settings?.activity,
			TabContent: ActivityTab,
		},
		{
			key: 'assignment',
			label: t('settings.assignment.short', { defaultValue: 'Assignment' }),
			hasError: (errors: FieldErrors<PricingCampaignFormData>) =>
				errors?.settings?.product_assignments,
			TabContent: AssignmentTab,
		},
		{
			key: 'tactics',
			label: t('settings.tactics.short', { defaultValue: 'Tactics' }),
			hasError: (errors: FieldErrors<PricingCampaignFormData>) =>
				errors?.name || errors?.settings?.pricing_tactics,
			TabContent: PricingCampaignTacticsTab,
		},

		{
			key: 'repricing_limits',
			label: t('settings.price_limits.short', { defaultValue: 'Limits' }),
			hasError: (errors: FieldErrors<PricingCampaignFormData>) =>
				errors?.settings?.price_limits,
			TabContent: RepricingLimitsTab,
		},
		{
			key: 'anchoring',
			label: t('settings.anchoring.short', { defaultValue: 'Anchoring' }),
			hasError: (errors: FieldErrors<PricingCampaignFormData>) =>
				errors?.settings?.anchoring,
			TabContent: AnchoringTab,
		},
		{
			key: 'rounding',
			label: t('settings.rounding.short', { defaultValue: 'Rounding' }),
			hasError: (errors: FieldErrors<PricingCampaignFormData>) =>
				errors?.settings?.rounding,
			TabContent: RoundingRulesTab,
		},
		{
			key: 'min_steps',
			label: t('settings.minimal_steps.short', {
				defaultValue: 'Minimal steps',
			}),
			hasError: (errors: FieldErrors<PricingCampaignFormData>) =>
				errors?.settings?.minimal_steps,
			TabContent: MinimalPriceStepTab,
		},
	].filter(Boolean)

const filterTemplateActions = (actions: ActionType[]) =>
	filter(({ key }) => key !== 'apply_template', actions)

const useActions = (templateType: SettingsTemplateType) => {
	const { watch, setValue } = useFormContext()
	const context = usePricingCampaignContext()
	const { path, name } = useMemo(() => {
		switch (templateType) {
			case SettingsTemplateType.PricingCampaignLimits:
				return {
					path: 'settings.price_limits',
					name: 'price_limits',
				}
			case SettingsTemplateType.PricingCampaignProductAssignment:
				return {
					path: 'settings.product_assignments',
					name: 'product_assignments',
				}
			case SettingsTemplateType.PricingCampaignAnchoring:
				return {
					path: 'settings.anchoring',
					name: 'anchoring',
				}
			case SettingsTemplateType.PricingCampaignRounding:
				return {
					path: 'settings.rounding',
					name: 'rounding',
				}
			case SettingsTemplateType.PricingCampaignMinimalSteps:
				return {
					path: 'settings.minimal_steps',
					name: 'minimal_steps',
				}
			case SettingsTemplateType.PricingCampaignActivity:
				return {
					path: 'settings.activity',
					name: 'activity',
				}
			case SettingsTemplateType.PricingCampaignTacticsRb:
			case SettingsTemplateType.PricingCampaignTacticsMd:
			case SettingsTemplateType.PricingCampaignTacticsPromo:
			case SettingsTemplateType.PricingCampaignTacticsMl:
				return {
					path: 'settings.pricing_tactics',
					name: 'pricing_tactics',
				}

			default:
				return {
					path: `settings.${templateType}`,
					name: templateType,
				}
		}
	}, [templateType])
	const allowedTypes = useMemo(() => {
		if (
			[
				SettingsTemplateType.PricingCampaignTacticsRb,
				SettingsTemplateType.PricingCampaignTacticsMd,
				SettingsTemplateType.PricingCampaignTacticsPromo,
				SettingsTemplateType.PricingCampaignTacticsMl,
			].includes(templateType)
		) {
			return [
				SettingsTemplateType.PricingCampaignTacticsRb,
				SettingsTemplateType.PricingCampaignTacticsMd,
				SettingsTemplateType.PricingCampaignTacticsPromo,
				SettingsTemplateType.PricingCampaignTacticsMl,
			]
		}

		return [templateType]
	}, [templateType])
	const isNew = context.isNew
	const handleUseTemplate = ({ body }: SettingsTemplateModel) => {
		// Older templates store RB params as `rule_id`; the tactics fields read the
		// normalized shape, so apply the same migration the campaign load does.
		setValue(path, formatLegacyTactics(body[name as keyof typeof body]), {
			shouldDirty: true,
		})
	}
	const templateActions = useTemplateActions({
		allowedTypes,
		applyAction: ApplyAction.UpdatePricingCampaignSettings,
		templatePayload: {
			template_type: templateType,
			name: watch('name'),
			body: {
				[name]: watch(path),
			},
		},
		handleUseTemplate,
		optimizationGroupId: watch('optimization_group_id'),
		id: watch('id'),
	})
	const settingActions = useSettingsActions({ settingPaths: [path] })

	return useMemo(
		() =>
			[...settingActions, ...filterTemplateActions(templateActions)].filter(
				({ key }) => (isNew ? NEW_PC_ACTION_KEYS.includes(key) : true)
			),
		[settingActions, templateActions, isNew]
	)
}
const getSettingType = (engine: string) => {
	switch (engine) {
		case PricingEngineType.ML:
			return SettingsTemplateType.PricingCampaignTacticsMl
		case PricingEngineType.MD:
			return SettingsTemplateType.PricingCampaignTacticsMd
		case PricingEngineType.RB:
			return SettingsTemplateType.PricingCampaignTacticsRb
		case PricingEngineType.Promo:
			return SettingsTemplateType.PricingCampaignTacticsPromo
		default:
			return SettingsTemplateType.PricingCampaignTacticsMl
	}
}
function PricingCampaignTacticsTab() {
	const { t } = useTranslation()

	const { watch } = useFormContext()
	const { optimizationGroupId } = usePricingCampaignContext()
	const engine = watch('settings.pricing_tactics.engine')
	const paramsPath = 'settings.pricing_tactics.params'
	const actions = useActions(getSettingType(engine))

	return (
		<div className='space-y-5'>
			<TargetPriceTypeController
				engine={engine}
				name='settings.pricing_tactics.target_price_type'
			/>
			{[PricingEngineType.ML, PricingEngineType.MD].includes(engine) && (
				<Card>
					<CardHeader className='grid-rows-1'>
						<CardTitle>
							{t('pc.pricing_tactics.inheritance.title', {
								defaultValue: 'Pricing strategy inheritance',
							})}
						</CardTitle>
						<CardDescription>
							{t('pc.pricing_tactics.inheritance.subtitle', {
								defaultValue:
									'Pricing strategy inheritance from optimization group settings',
							})}
						</CardDescription>
						<CardAction className='flex items-center gap-2'>
							<VideoTutorials tags={[VideoTag.Tactics]}>
								<Button className='ml-auto' variant='ghost' size='icon-sm'>
									<InfoIcon />
								</Button>
							</VideoTutorials>
							<ActionsDropdown actions={actions}>
								<Button variant='ghost' size='icon-sm'>
									<EllipsisVerticalIcon />
								</Button>
							</ActionsDropdown>
						</CardAction>
					</CardHeader>
					<CardContent>
						{engine === PricingEngineType.ML && <PricingTacticDemandFields />}
						{engine === PricingEngineType.MD && <PricingTacticMarkdowFields />}
					</CardContent>
				</Card>
			)}
			{[
				PricingEngineType.RB,
				PricingEngineType.MD,
				PricingEngineType.Promo,
			].includes(engine) && (
				<Card>
					<CardHeader>
						<CardTitle>
							{t('pc.pricing_tactics', { defaultValue: 'Pricing tactics' })}
						</CardTitle>
						<CardAction className='flex items-center gap-2'>
							<VideoTutorials tags={[VideoTag.Tactics]}>
								<Button className='ml-auto' variant='ghost' size='icon-sm'>
									<InfoIcon />
								</Button>
							</VideoTutorials>
							<ActionsDropdown actions={actions}>
								<Button variant='ghost' size='icon-sm'>
									<EllipsisVerticalIcon />
								</Button>
							</ActionsDropdown>
						</CardAction>
					</CardHeader>
					<CardContent>
						{engine === PricingEngineType.MD && (
							<MarkdownPeriodsFieldController name={paramsPath} />
						)}
						{engine === PricingEngineType.RB && (
							<Suspense
								fallback={
									<div className='text-sm text-muted-foreground'>
										{t('loading')}
									</div>
								}
							>
								<PricingTacticRuleFieldController
									name={paramsPath}
									context={{
										optimizationGroupId: optimizationGroupId ?? undefined,
									}}
								/>
							</Suspense>
						)}
						{engine === PricingEngineType.Promo && (
							<PromoPeriodFieldController name={paramsPath} />
						)}
					</CardContent>
				</Card>
			)}
		</div>
	)
}
function RepricingLimitsTab() {
	const { t } = useTranslation()

	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('entity:pricing_campaign'),
			prop('properties.settings.properties.price_limits'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})

	const schema = settingsSchema.data
	const actions = useActions(SettingsTemplateType.PricingCampaignLimits)

	if (settingsSchema.isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}
	if (settingsSchema.error?.message) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>{settingsSchema.error?.message}</AlertDescription>
			</Alert>
		)
	}

	return (
		<Card data-testid='pc-repricing-limits-panel' className='relative w-full'>
			<CardHeader className='grid-rows-1'>
				<CardTitle>{prop('title', schema)}</CardTitle>
				{actions && (
					<CardAction className='flex items-center gap-2'>
						<PricingCampaignLimitsExampleSheet />
						<VideoTutorials tags={[VideoTag.RepricingLimits]}>
							<Button className='ml-auto' variant='ghost' size='icon-sm'>
								<InfoIcon />
							</Button>
						</VideoTutorials>

						<ActionsDropdown actions={actions}>
							<Button
								data-testid='pc-actions-popover-trigger'
								variant='ghost'
								size='icon-sm'
							>
								<EllipsisVerticalIcon />
							</Button>
						</ActionsDropdown>
					</CardAction>
				)}
			</CardHeader>
			<CardContent className='space-y-5'>
				<ErrorBoundary>
					<PriceLimitsFieldController
						name='settings.price_limits.lower'
						schema={prop('properties.lower', schema)}
					/>
					<PriceLimitsFieldController
						name='settings.price_limits.upper'
						schema={prop('properties.upper', schema)}
					/>
				</ErrorBoundary>
			</CardContent>
			<CardHeader>
				<CardTitle>{prop('properties.extra.title', schema)}</CardTitle>
				<CardDescription>
					{prop('properties.extra.description', schema)}
				</CardDescription>
			</CardHeader>
			<CardContent>
				<RHFSelect
					name='settings.price_limits.extra.on_conflict'
					defaultValue={propOr(
						[],
						'properties.extra.properties.on_conflict.default',
						schema
					)}
					label={prop('properties.extra.properties.on_conflict.title', schema)}
					options={getEnumOptions(
						prop('properties.extra.properties.on_conflict', schema)
					)}
				/>
			</CardContent>
		</Card>
	)
}

function AssignmentPredictedCount() {
	const { methods, pricingCampaign, scenarioId } = usePricingCampaignContext()
	const settings = useWatch({
		name: 'settings',
		control: methods.control,
	})

	return (
		<PricingCampaignAssignmentPrediction
			id={pricingCampaign?.id || '00000000-0000-0000-0000-000000000000'}
			/* 
				NOTE: use structuredClone to avoid reference issues for correct debouncing
				of the settings object.
			*/
			/* eslint-disable-next-line @eslint-react/purity */
			settings={settings ? structuredClone(settings) : settings}
			scenarioId={scenarioId}
			order={pricingCampaign?.order}
		/>
	)
}
function AssignmentTab() {
	const { t } = useTranslation()

	const { isNew, draft } = usePricingCampaignContext()
	const actions = useActions(
		SettingsTemplateType.PricingCampaignProductAssignment
	)

	return (
		<ErrorBoundary>
			<div className='grid gap-1.5'>
				{isNew && draft?.assignment_filters && (
					<div className='p-4'>
						<Alert variant='info'>
							<InfoIcon />
							<AlertTitle>
								{t('pc.assignment_filters_hint', {
									defaultValue:
										'Ensure the pricing campaign has top priority to prevent product allocation to other campaigns via dynamic filters',
								})}
							</AlertTitle>
						</Alert>
					</div>
				)}
				<Card>
					<CardHeader className='grid-rows-1'>
						<CardTitle>
							{t('settings.assignment.long', {
								defaultValue: 'Product assignment',
							})}
						</CardTitle>
						{actions && (
							<CardAction className='flex items-center gap-2'>
								<AssignmentPredictedCount />
								<VideoTutorials tags={[VideoTag.DynamicAssignment]}>
									<Button className='ml-auto' variant='ghost' size='icon-sm'>
										<InfoIcon />
									</Button>
								</VideoTutorials>
								<ActionsDropdown actions={actions}>
									<Button
										data-testid='pc-actions-popover-trigger'
										variant='ghost'
										size='icon-sm'
									>
										<EllipsisVerticalIcon />
									</Button>
								</ActionsDropdown>
							</CardAction>
						)}
					</CardHeader>
					<CardContent>
						<AssignmentFieldsController name='settings.product_assignments' />
					</CardContent>
				</Card>
			</div>
		</ErrorBoundary>
	)
}
function ActivityTab() {
	const { t } = useTranslation()

	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('entity:pricing_campaign'),
			prop('properties.settings.properties.activity'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	const actions = useActions(SettingsTemplateType.PricingCampaignActivity)

	return (
		<Card data-testid='pc-anchoring-panel'>
			<CardHeader className='grid-rows-1'>
				<CardTitle>
					{t('settings.activity.long', { defaultValue: 'Activity' })}
				</CardTitle>
				{actions && (
					<CardAction>
						<VideoTutorials
							tags={[
								VideoTag.CampaignActivity,
								VideoTag.PricingCampaignSettings,
							]}
						>
							<Button className='ml-auto' variant='ghost' size='icon-sm'>
								<InfoIcon />
							</Button>
						</VideoTutorials>
						<ActionsDropdown actions={actions}>
							<Button
								data-testid='pc-actions-popover-trigger'
								variant='ghost'
								size='icon-sm'
							>
								<EllipsisVerticalIcon />
							</Button>
						</ActionsDropdown>
					</CardAction>
				)}
			</CardHeader>
			<CardContent className='grid gap-4'>
				<ActivityFields name='settings.activity' schema={settingsSchema.data} />
			</CardContent>
		</Card>
	)
}

function AnchoringTab() {
	const { t } = useTranslation()

	const {
		data: schema,
		isLoading,
		error,
	} = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('entity:pricing_campaign'),
			prop('properties.settings.properties.anchoring'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	const actions = useActions(SettingsTemplateType.PricingCampaignAnchoring)
	if (isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}
	if (error?.message) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>{error?.message}</AlertDescription>
			</Alert>
		)
	}

	return (
		<Card data-testid='pc-anchoring-panel'>
			<CardHeader className='grid-rows-1'>
				<CardTitle>
					{t('anchoring.section.beahvior.title', {
						defaultValue: 'Anchoring price behavior',
					})}
				</CardTitle>
				{actions && (
					<CardAction className='flex items-center gap-2'>
						<PricingCampaignAnchoringExampleSheet />
						<VideoTutorials tags={[VideoTag.AnchoringBehavior]}>
							<Button className='ml-auto' variant='ghost' size='icon-sm'>
								<InfoIcon />
							</Button>
						</VideoTutorials>
						<ActionsDropdown actions={actions}>
							<Button
								data-testid='pc-actions-popover-trigger'
								variant='ghost'
								size='icon-sm'
							>
								<EllipsisVerticalIcon />
							</Button>
						</ActionsDropdown>
					</CardAction>
				)}
			</CardHeader>
			<CardContent>
				<AnchoringRulesFiledController name='settings.anchoring' />
			</CardContent>
			<CardHeader>
				<CardTitle>
					{t('anchoring.section.additional_settings.title', {
						defaultValue: 'Additional settings',
					})}
				</CardTitle>
				<CardDescription>
					{t('anchoring.section.additional_settings.subtitle', {
						defaultValue: 'Define the prior limit parameter in special cases.',
					})}
				</CardDescription>
			</CardHeader>
			<CardContent className='grid gap-4'>
				<RHFSelect
					className='w-64'
					name='settings.anchoring.on_conflict'
					defaultValue={propOr([], 'properties.on_conflict.default', schema)}
					label={prop('properties.on_conflict.title', schema)}
					options={getEnumOptions(prop('properties.on_conflict', schema))}
				/>
				<RHFSelect
					className='w-64'
					name='settings.anchoring.on_conflict_anchor_limits'
					defaultValue={propOr(
						[],
						'properties.on_conflict_anchor_limits.default',
						schema
					)}
					label={prop('properties.on_conflict_anchor_limits.title', schema)}
					options={getEnumOptions(
						prop('properties.on_conflict_anchor_limits', schema)
					)}
				/>
				<RHFSelect
					className='w-64'
					name='settings.anchoring.on_anchor_locked'
					defaultValue={propOr(
						[],
						'properties.on_anchor_locked.default',
						schema
					)}
					label={prop('properties.on_anchor_locked.title', schema)}
					options={getEnumOptions(prop('properties.on_anchor_locked', schema))}
				/>
				<RHFSelect
					className='w-64'
					name='settings.anchoring.rounding_limits_and_price'
					defaultValue={propOr(
						[],
						'properties.rounding_limits_and_price.default',
						schema
					)}
					label={prop('properties.rounding_limits_and_price.title', schema)}
					options={getEnumOptions(
						prop('properties.rounding_limits_and_price', schema)
					)}
				/>
			</CardContent>
		</Card>
	)
}
function RoundingRulesTab() {
	const { t } = useTranslation()

	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('entity:pricing_campaign'),
			prop('properties.settings.properties.rounding'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	const schema = settingsSchema.data
	const actions = useActions(SettingsTemplateType.PricingCampaignRounding)
	if (settingsSchema.isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}
	if (settingsSchema.error?.message) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>{settingsSchema.error?.message}</AlertDescription>
			</Alert>
		)
	}

	return (
		<Card data-testid='pc-rounding-panel'>
			<CardHeader className='grid-rows-1'>
				<CardTitle>{t('rounding_block_title')}</CardTitle>
				{actions && (
					<CardAction className='flex items-center gap-2'>
						<PricingCampaignRoundingExampleSheet />
						<VideoTutorials tags={[VideoTag.RoundingRules]}>
							<Button className='ml-auto' variant='ghost' size='icon-sm'>
								<InfoIcon />
							</Button>
						</VideoTutorials>
						<ActionsDropdown actions={actions}>
							<Button
								data-testid='pc-actions-popover-trigger'
								variant='ghost'
								size='icon-sm'
							>
								<EllipsisVerticalIcon />
							</Button>
						</ActionsDropdown>
					</CardAction>
				)}
			</CardHeader>
			<CardContent>
				<RoundingRulesFiledController
					name='settings.rounding'
					schema={schema}
				/>
			</CardContent>
		</Card>
	)
}
function MinimalPriceStepTab() {
	const { t } = useTranslation()

	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('entity:pricing_campaign'),
			prop('properties.settings.properties.minimal_steps'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	const schema = settingsSchema.data
	const actions = useActions(SettingsTemplateType.PricingCampaignMinimalSteps)

	if (settingsSchema.isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}
	if (settingsSchema.error?.message) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>{settingsSchema.error?.message}</AlertDescription>
			</Alert>
		)
	}

	return (
		<Card data-testid='pc-min-steps-panel'>
			<CardHeader className='grid-row-1'>
				<CardTitle>{schema?.title}</CardTitle>
				{actions && (
					<CardAction className='flex items-center gap-2'>
						<VideoTutorials tags={[VideoTag.MinimalStep]}>
							<Button className='ml-auto' variant='ghost' size='icon-sm'>
								<InfoIcon />
							</Button>
						</VideoTutorials>
						<ActionsDropdown actions={actions}>
							<Button
								data-testid='pc-actions-popover-trigger'
								variant='ghost'
								size='icon-sm'
							>
								<EllipsisVerticalIcon />
							</Button>
						</ActionsDropdown>
					</CardAction>
				)}
			</CardHeader>
			<CardContent>
				<MinimalPriceStepFiledController
					name='settings.minimal_steps'
					schema={schema}
				/>
			</CardContent>
		</Card>
	)
}
